import { AssistantDisplay } from '@/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { RelativeTime } from '@/components/RelativeTime';
import { useEffect, useState } from 'react';
import FirebaseService from '@/lib/firebase';
import { ChevronRight } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface EmissaryListCompactProps {
  bots: AssistantDisplay[];
  currentUser?: any;
  onView?: (bot: AssistantDisplay) => void;
}

interface BotWithMessage extends AssistantDisplay {
  lastMessage?: string;
  lastMessageTime?: Date;
}

// Utility function to truncate message text
const truncateMessage = (message: string, maxWords: number = 30): string => {
  const words = message.split(' ');
  if (words.length <= maxWords) return message;
  return words.slice(0, maxWords).join(' ') + '...';
};

export function EmissaryListCompact({ 
  bots, 
  currentUser, 
  onView 
}: EmissaryListCompactProps) {
  const [botsWithMessages, setBotsWithMessages] = useState<BotWithMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const fetchLastMessages = async () => {

      setLoading(true);
      
      // Add a small delay to ensure Firebase is fully initialized
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const updatedBots: BotWithMessage[] = [];

      for (const bot of bots) {
        let lastMessage = '';
        let lastMessageTime = bot.createdAt || new Date();

        try {
          
          // Use FirebaseService to fetch messages with proper authentication
          const messages = await FirebaseService.getMessages(bot.assistantId, 1);
          
          //console.log(`Fetched ${messages.length} messages for ${bot.name}`);
          
          if (messages && messages.length > 0) {
            // Get the last message (most recent)
            const lastMsg = messages[messages.length - 1];
            //console.log(`Message structure for ${bot.name}:`, lastMsg);
            lastMessage = lastMsg.content || '';
            lastMessageTime = lastMsg.timestamp ? new Date(lastMsg.timestamp) : lastMessageTime;
            //console.log(`Last message for ${bot.name}:`, lastMessage.substring(0, 50));
          } else {
            console.log(`No messages found for ${bot.name}`);
          }
        } catch (error) {
          console.error(`Error fetching messages for bot ${bot.assistantId}:`, error);
          // For debugging, log the full error
          console.log('Full error:', JSON.stringify(error, null, 2));
        }

        updatedBots.push({
          ...bot,
          lastMessage,
          lastMessageTime
        });
      }

      // Sort by last message time
      updatedBots.sort((a, b) => (b.lastMessageTime?.getTime() || 0) - (a.lastMessageTime?.getTime() || 0));
      
      setBotsWithMessages(updatedBots);
      setLoading(false);
    };

    // Only fetch if we have bots
    if (bots.length > 0) {
      fetchLastMessages();
    } else {
      setLoading(false);
    }
  }, [bots, currentUser]);

  const handleView = (bot: AssistantDisplay) => {
    if (onView) {
      onView(bot);
    } else {
      router.push(`/assistant/${bot.assistantId}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  //console.log('EmissaryListCompact - Rendering with messages:', botsWithMessages.map(bot => ({
  //  name: bot.name,
  //  hasMessage: !!bot.lastMessage,
  //  message: bot.lastMessage?.substring(0, 30)
  //})));

  return (
    <div className="space-y-3">
      {botsWithMessages.map((bot) => (
        <div
          key={bot.assistantId}
          onClick={() => handleView(bot)}
          className="flex items-center space-x-3 p-4 hover:bg-muted/50 transition-colors cursor-pointer rounded-lg border border-border"
        >
          <Avatar className="h-12 w-12">
            <AvatarImage src={bot.avatar} alt={bot.name} />
            <AvatarFallback>{bot.name?.[0] || '?'}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-medium truncate text-base">
                {bot.name}
              </h3>
              {bot.lastMessageTime && (
                <span className="text-xs text-muted-foreground">
                  <RelativeTime date={bot.lastMessageTime} />
                </span>
              )}
            </div>
            {bot.lastMessage ? (
              <p className="text-muted-foreground text-sm line-clamp-2">
                {truncateMessage(bot.lastMessage)}
              </p>
            ) : (
              <p className="text-muted-foreground text-sm italic">
                No messages yet
              </p>
            )}
          </div>
          
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      ))}
    </div>
  );
}

export default EmissaryListCompact;